import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Buton,
  DersHedefFarki,
  Iskelet,
  Kart,
  KucukCizgi,
  denemeTurAdi,
  isaretliFark,
  netMetni,
} from "@koclukpanel/ui";
import {
  analizTrendi,
  atamaListesi,
  denemeTrendi,
  haftalikAnaliz,
  ogrenciGorusmeleri,
  ogrenciKunyesi,
  onboardingDurumu,
} from "../api";
import { HedefFormu } from "./HedefFormu";
import { HedefNetKarti } from "./HedefNetKarti";
import { PuanKarti } from "./PuanKarti";
import { SiparisKarti } from "./SiparisKarti";
import { TanimaNotuKarti } from "./TanimaNotuKarti";
import type { SekmeOzellikleri } from "./sekmeler";

/**
 * GENEL BAKIŞ — F-PPL-05
 *
 * ================================================================
 * TEK UÇ DEĞİL, ALTI UÇ — VE BU BİLİNÇLİ
 * ================================================================
 * Şartname dokuz şey istiyor. İlk tasarım hepsini tek bir
 * `GET /ogrenciler/:id/kunye` ucunda toplamaktı. 05.09.2026'da tek tek
 * ölçüldü: yedisinin ZATEN ucu var ve her biri kendi servisinde
 * KAYIT BAZLI erişim kontrolü + kendi denetim olayı taşıyor
 * (`gorusme.service.ts` -> `erisebilirMi`, `deneme.service.ts` ve
 * `analiz.service.ts` -> `ogrenciErisimi`).
 *
 * Tek uçta toplamak, "bu kişi bu öğrencinin denemesini görebilir mi"
 * sorusunun BEŞİNCİ evini açardı. Bu depoda kopyalanan bir kuralın
 * kopyalarından biri değişip ötekinin sessizce ayrılması defalarca
 * görüldü — `aktifAtamaFiltresi` on üç ayrı serviste tekrar yazılmıştı
 * ve biri ötekine uymuyordu.
 *
 * Bedeli altı paralel istek. Kazancı: izinsiz bir bölüm İSTEK BİLE
 * ATMIYOR, yani sunucudan o veri hiç çıkmıyor.
 *
 * ================================================================
 * BÖLÜM İZNİ YOKSA BÖLÜM ÇİZİLMİYOR — BOŞ KUTU DA YOK
 * ================================================================
 * "Yetkiniz yok" kutusu koymak, o verinin VAR OLDUĞUNU söyler. Bir
 * öğrencinin deneme sonucu olup olmadığı da korunması gereken bir
 * bilgi. Bu yüzden izinsiz bölüm hiç çizilmiyor ve isteği de atılmıyor.
 *
 * ================================================================
 * "VERİ YOK" İLE "YETKİN YOK" AYRI ŞEYLER
 * ================================================================
 * İzin varsa bölüm çiziliyor ve veri boşsa bunu AÇIKÇA yazıyor
 * ("Henüz deneme sonucu yok"). Sıfır göstermek yerine cümle yazmak
 * bu depodaki tekrar eden kuralın gereği: ölçülmemiş bir şeyi sıfır
 * gibi göstermek, uzmana "sorun yok" dedirtiyor.
 */

/** `null` sayıyı SAYI GİBİ göstermemek için — sıfır bir ölçüm sonucudur. */
function yuzde(oran: number | null): string | null {
  return oran === null ? null : `%${Math.round(oran * 100)}`;
}

function gunMetni(kalan: number | null): string {
  if (kalan === null) return "Sınav tarihi girilmemiş";
  if (kalan === 0) return "Sınav BUGÜN";
  if (kalan < 0) return `Sınav ${Math.abs(kalan)} gün önceydi`;
  return `${kalan} gün kaldı`;
}

export function GenelBakis({ ogrenciId, izinVar }: SekmeOzellikleri) {
  const [hedefDuzenle, setHedefDuzenle] = useState(false);

  /*
    ================================================================
    HER SORGU KENDİ İZNİNE BAĞLI — `enabled` İLE
    ================================================================
    `enabled: false` olan bir sorgu react-query'de HİÇ çalışmıyor:
    ağa istek gitmiyor. Yani izinsiz bölüm için sunucuya soru bile
    sorulmuyor. Veriyi çekip ekranda gizlemek, "gizlemek" değildir.
  */
  const kunye = useQuery({
    queryKey: ["ogrenci-kunye", ogrenciId],
    queryFn: () => ogrenciKunyesi(ogrenciId),
    retry: false,
  });

  const atamalar = useQuery({
    queryKey: ["ogrenci-atamalar", ogrenciId],
    queryFn: () => atamaListesi(ogrenciId),
    retry: false,
  });

  const onboarding = useQuery({
    queryKey: ["onboarding", ogrenciId],
    queryFn: () => onboardingDurumu(ogrenciId),
    retry: false,
  });

  const gorusmeler = useQuery({
    queryKey: ["gorusmeler", ogrenciId],
    queryFn: () => ogrenciGorusmeleri(ogrenciId),
    retry: false,
    enabled: izinVar("gorusme.kayit.goruntule"),
  });

  const denemeler = useQuery({
    queryKey: ["deneme-trendi", ogrenciId],
    queryFn: () => denemeTrendi(ogrenciId),
    retry: false,
    enabled: izinVar("deneme.sonuc.goruntule"),
  });

  const analiz = useQuery({
    queryKey: ["haftalik-analiz", ogrenciId, "bu-hafta"],
    queryFn: () => haftalikAnaliz(ogrenciId),
    retry: false,
    enabled: izinVar("analiz.ogrenci.goruntule"),
  });

  /*
    DÖRT HAFTALIK TREND — dalga 3. Dört ayrı `/hafta` isteği değil, tek uç
    (`/analiz/ogrenci/:id/trend`); izin `/hafta` ile aynı.
  */
  const trend = useQuery({
    queryKey: ["analiz-trend", ogrenciId, 4],
    queryFn: () => analizTrendi(ogrenciId, 4),
    retry: false,
    enabled: izinVar("analiz.ogrenci.goruntule"),
  });

  const uzman = atamalar.data?.find((a) => a.gecerli && a.isPrimary);
  const sonGorusme = gorusmeler.data?.[0];
  /*
    SON DENEME = DİZİNİN SONU. Trend ucu denemeleri ESKİDEN YENİYE döndürüyor
    (`deneme.service.ts` → `.reverse()`); burası `[0]` okuyordu ve "Son deneme
    neti" kartı en ESKİ denemeyi gösteriyordu (dalga 3'te grafik bağlanırken bulundu).
  */
  const sonDeneme = denemeler.data?.denemeler.at(-1);
  /* Küçük çizgi yalnız SON denemenin türünden: TYT ile AYT aynı çizgide birleşmez. */
  const ayniTur = sonDeneme
    ? (denemeler.data?.denemeler ?? []).filter((d) => d.tur === sonDeneme.tur)
    : [];
  const haftalar = trend.data?.haftalar ?? [];
  const yuzdeDegeri = (o: number | null) => (o === null ? null : Math.round(o * 1000) / 10);

  return (
    <div className="flex flex-col gap-4">
      {/*
        HEDEF BLOĞU — 360° ekranının tek kendi ucu.

        ================================================================
        BİR TUR BOYUNCA HER ÖĞRENCİDE "GİRİLMEMİŞ" YAZIYORDU
        ================================================================
        05.09.2026'da ölçüldü: `targetExamKind`, `targetScore`,
        `targetRank`, `targetSchool` ve `examDate` şemada duruyordu ama
        onları YAZAN hiçbir uç, hiçbir form yoktu.

        Kartı o hâlde çizdim — çizmemek eksiği GÖRÜNMEZ kılardı, oysa
        bu ekranın varlık sebebi "ne var ne yok"u göstermek. Boşluk
        görünür olunca da doldurulacak yer belli oldu: düzenleme
        formu bu kartın içinde (`HedefFormu.tsx`).
      */}
      <Kart
        baslik="Hedef"
        aciklama="Sınav, puan ve hedef okul" /* F-PPL-05 */
        /*
          DUZENLE DUGMESI IZNE BAGLI. `kisi.ogrenci.duzenle` — hedefi
          degistiren uc (`PATCH /ogrenciler/:id`) de aynisini istiyor.
          Farkli bir izne baglasaydik ekran ya calismayacak bir dugme
          gosterir ya da yetkili birinden gizlerdi.
        */
        eylem={
          izinVar("kisi.ogrenci.duzenle") && !hedefDuzenle && kunye.data !== undefined ? (
            <Buton gorunum="ikincil" onClick={() => setHedefDuzenle(true)}>
              Hedefi düzenle
            </Buton>
          ) : undefined
        }
      >
        {kunye.isPending ? (
          <Iskelet satir={2} />
        ) : kunye.isError ? (
          <p className="text-hata-700 text-sm">Hedef bilgisi alınamadı.</p>
        ) : hedefDuzenle ? (
          <HedefFormu
            ogrenciId={ogrenciId}
            hedef={kunye.data.hedef}
            alan={kunye.data.alan}
            onKapat={() => setHedefDuzenle(false)}
          />
        ) : (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <Satir etiket="Alan" deger={kunye.data.alan?.ad ?? null} />
            <Satir etiket="Hedef sınav" deger={kunye.data.hedef.sinav} />
            <Satir
              etiket="Hedef puan"
              deger={kunye.data.hedef.puan === null ? null : String(kunye.data.hedef.puan)}
            />
            <Satir
              etiket="Hedef sıralama"
              deger={kunye.data.hedef.siralama === null ? null : String(kunye.data.hedef.siralama)}
            />
            <Satir etiket="Hedef okul" deger={kunye.data.hedef.okul} />
            <div className="col-span-2 flex gap-2 sm:col-span-4">
              <dt className="text-notr-600">Sınava</dt>
              <dd className="text-notr-900">{gunMetni(kunye.data.hedef.kalanGun)}</dd>
            </div>
          </dl>
        )}
      </Kart>

      {/*
        F-EXM-09 — ders bazlı hedef net. Bölüm KENDİ iznine bağlı: izin yoksa kart da istek de yok.
        Düzenleme ayrı izin (`deneme.hedef.duzenle`): izin yoksa salt okur, form hiç çizilmez.
      */}
      {izinVar("deneme.hedef.goruntule") && (
        <HedefNetKarti ogrenciId={ogrenciId} duzenleyebilir={izinVar("deneme.hedef.duzenle")} />
      )}

      {/* Sürüm 9a — öğrenci tanıma notu; personel görür, öğrenci/veli GÖRMEZ (matris §5.6). */}
      {izinVar("kisi.not.goruntule") && (
        <TanimaNotuKarti
          ogrenciId={ogrenciId}
          duzenleyebilir={izinVar("kisi.tanima_notu.duzenle")}
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/*
          Atanmış uzman ve eksik işlemler `kisi.ogrenci.goruntule` ile
          geliyor — bu ekranı AÇMA izniyle aynısı. Ayrı bir `izinVar`
          sorusu tiyatro olurdu: sekmeyi gören zaten bu izne sahip.
        */}
        <Kart baslik="Atanmış uzman">
          {atamalar.isPending ? (
            <Iskelet satir={1} />
          ) : atamalar.isError ? (
            <p className="text-hata-700 text-sm">Atama bilgisi alınamadı.</p>
          ) : uzman ? (
            <p className="text-notr-900 text-sm font-medium">
              {uzman.firstName} {uzman.lastName}
            </p>
          ) : (
            /*
              "Atanmamış" bir DURUM, bir hata değil — ama sessiz de
              geçilemez: uzmansız öğrenci operasyonun görmesi gereken
              bir boşluk.
            */
            <p className="text-uyari-600 text-sm">Birincil uzman atanmamış</p>
          )}
        </Kart>

        <Kart baslik="Eksik işlemler" aciklama="Onboarding kontrol listesi" /* F-PPL-09 */>
          {onboarding.isPending ? (
            <Iskelet satir={1} />
          ) : onboarding.isError ? (
            <p className="text-hata-700 text-sm">Onboarding durumu alınamadı.</p>
          ) : onboarding.data.toplam === 0 ? (
            <p className="text-notr-600 text-sm">Kontrol listesi maddesi yok.</p>
          ) : (
            <div className="flex flex-col gap-1 text-sm">
              <p className="text-notr-900">
                {onboarding.data.tamamlanan} / {onboarding.data.toplam} tamamlandı
                {/* `oran` null ise yuzde YAZILMIYOR — "%0" demek olurdu. */}
                {yuzde(onboarding.data.oran) !== null && (
                  <span className="text-notr-600"> · {yuzde(onboarding.data.oran)}</span>
                )}
              </p>
              {onboarding.data.geciken > 0 && (
                <p className="text-uyari-600">{onboarding.data.geciken} madde gecikmiş</p>
              )}
            </div>
          )}
        </Kart>

        {/*
          Buradan sonraki üç kart KENDİ izinlerine bağlı. İzin yoksa
          `false` dönüyor ve kart hiç çizilmiyor — devre dışı bir kutu
          bile kalmıyor.
        */}
        {izinVar("gorusme.kayit.goruntule") && (
          <Kart baslik="Son görüşme">
            {gorusmeler.isPending ? (
              <Iskelet satir={1} />
            ) : gorusmeler.isError ? (
              <p className="text-hata-700 text-sm">Görüşmeler alınamadı.</p>
            ) : sonGorusme ? (
              <div className="flex flex-col gap-1 text-sm">
                <p className="text-notr-900">
                  {new Date(sonGorusme.scheduledAt).toLocaleDateString("tr-TR")}
                </p>
                {/*
                  ÖZET SUNUCUDA SÜZÜLÜYOR: `summary` görünürlük kuralından
                  geçmezse `null` geliyor (F-SES-04). Burada tekrar
                  süzmüyoruz — kural iki yerde yaşasaydı biri değiştiğinde
                  öteki sessizce eskirdi.

                  `null` gelmesi "özet yok" ile "özeti göremezsin"i
                  ayırmıyor; bu ayrımı bu kart YAPMIYOR çünkü ayrımı
                  yapmak, göremediği bir özetin VAR olduğunu söylemek
                  olurdu.
                */}
                {sonGorusme.summary ? (
                  <p className="text-notr-700 line-clamp-3">{sonGorusme.summary}</p>
                ) : (
                  <p className="text-notr-600">Bu görüşmenin özeti görüntülenemiyor.</p>
                )}
              </div>
            ) : (
              <p className="text-notr-600 text-sm">Henüz görüşme kaydı yok.</p>
            )}
          </Kart>
        )}

        {izinVar("deneme.sonuc.goruntule") && (
          <Kart baslik="Son deneme neti">
            {denemeler.isPending ? (
              <Iskelet satir={1} />
            ) : denemeler.isError ? (
              <p className="text-hata-700 text-sm">Deneme sonuçları alınamadı.</p>
            ) : sonDeneme ? (
              <div className="flex flex-col gap-1 text-sm">
                <p className="text-notr-900 text-lg font-semibold">{sonDeneme.net}</p>
                <p className="text-notr-600">{sonDeneme.ad}</p>
                {/* F-EXM-09 — hedef ve fark SUNUCUDAN (`hedefNet`); burada hesaplanmıyor. */}
                {sonDeneme.hedefNet != null && (
                  <p className="text-notr-700">
                    Hedef {netMetni(sonDeneme.hedefNet.toplamHedef)} net;{" "}
                    {isaretliFark(sonDeneme.hedefNet.toplamFark)} fark
                  </p>
                )}
                {sonDeneme.hedefNet != null && sonDeneme.hedefNet.dersler.length > 0 && (
                  <DersHedefFarki dersler={sonDeneme.hedefNet.dersler} aciklama={sonDeneme.ad} />
                )}
                {ayniTur.length > 1 && (
                  <KucukCizgi
                    baslik={`${denemeTurAdi(sonDeneme.tur)} neti, son ${ayniTur.length} deneme`}
                    birim="net"
                    noktalar={ayniTur.map((d) => ({ deger: d.net, tarih: d.tarih }))}
                  />
                )}
              </div>
            ) : (
              <p className="text-notr-600 text-sm">Henüz deneme sonucu yok.</p>
            )}
          </Kart>
        )}

        {izinVar("analiz.ogrenci.goruntule") && (
          <Kart baslik="Program tamamlama" aciklama="Bu hafta">
            {analiz.isPending ? (
              <Iskelet satir={1} />
            ) : analiz.isError ? (
              <p className="text-hata-700 text-sm">Haftalık analiz alınamadı.</p>
            ) : (
              /*
                ================================================================
                `SayiKarti` KULLANILMADI — ÇÜNKÜ EŞİĞİ BİLMİYORUM
                ================================================================
                `SayiKarti` bir HÜKÜM kartı: hâli "iyi" · "dikkat" ·
                "kritik" · "ölçülemedi". Tamamlama oranını oraya koymak,
                "%60 iyi mi kötü mü" sorusunu bu dosyada cevaplamak
                demekti. O eşik burada yazılmamalı: panelde ayrı bir
                `Akademik eşikler` ekranı var ve eşiği kuruma göre
                değiştirilebilir kılmak orada karara bağlanmış.

                Uydurulmuş bir eşik, kartı yeşile boyayıp uzmana "sorun
                yok" dedirtirdi. Sayı yalın yazılıyor; hükmü veren yok.
              */
              <div className="flex flex-col gap-1 text-sm">
                {/*
                  `tamamlamaOrani` `null` olabiliyor: hafta boyunca hiç
                  görev ATANMAMIŞSA oran tanımsız. "%0" yazmak öğrenciyi
                  hiç çalışmamış gösterirdi — oysa ortada program yok.
                */}
                {analiz.data.programUyumu.tamamlamaOrani === null ? (
                  <p className="text-notr-600">Bu hafta atanmış görev yok — oran hesaplanamıyor.</p>
                ) : (
                  <>
                    <p className="text-notr-900 text-lg font-semibold">
                      {yuzde(analiz.data.programUyumu.tamamlamaOrani)}
                    </p>
                    <p className="text-notr-600">
                      {analiz.data.programUyumu.tamamlanan} / {analiz.data.programUyumu.atanan}{" "}
                      görev
                    </p>
                  </>
                )}
                {haftalar.some((h) => h.tamamlamaOrani !== null) && (
                  <KucukCizgi
                    baslik="Program tamamlama, son 4 hafta"
                    birim="%"
                    noktalar={haftalar.map((h) => ({
                      deger: yuzdeDegeri(h.tamamlamaOrani),
                      tarih: h.haftaBasi.slice(0, 10),
                    }))}
                  />
                )}
              </div>
            )}
          </Kart>
        )}
        {/* F-GAM-15 — puan kartı yalnız izinle (veli rolünde oyun izni yok; koç atanmış öğrencisi). */}
        {izinVar("oyun.bakiye.goruntule") && (
          <PuanKarti ogrenciId={ogrenciId} verebilir={izinVar("oyun.puan.ver")} />
        )}
        {/* F-GAM-17 — mağaza siparişleri, salt okur (adres yok); kayıt yoksa kart çizilmez. */}
        {izinVar("oyun.siparis.goruntule") && <SiparisKarti ogrenciId={ogrenciId} />}
      </div>

      {/*
        ================================================================
        RİSK SEVİYESİ VE "BU HAFTANIN ÖNCELİKLERİ" BU EKRANDA YOK
        ================================================================
        F-PPL-05 dokuz madde sayıyor; burada yedisi var. Kalan ikisi
        BİLEREK çizilmiyor ve yer tutucu da konmuyor:

        • **risk seviyesi** — `RiskScore` modeli şemada var ama onu
          HESAPLAYAN kod yok. Boş bir kart "risk: düşük" gibi okunurdu;
          yani ölçülmemiş bir şey iyi haber gibi görünürdü. Bu ekranda
          en pahalıya patlayacak yanlış tam olarak budur.

        • **bu haftanın öncelikleri** — böyle bir model YOK. En yakını
          `BaselineAssessment.firstMonthGoals` (ilk AY) ve
          `CoachingSession.studentGoals` (o görüşmenin hedefi). Birini
          "bu haftanın önceliği" diye sunmak, belgeyi koda uydurmak
          olurdu (CLAUDE.md). Tanım netleşmeden alan açılmayacak.

        "Yakında" yazan gri bir kutu da konmadı: kullanıcıya bir söz
        verip tarihini söylememek, boşluğu göstermekten daha kötü.
      */}
    </div>
  );
}

function Satir({ etiket, deger }: { etiket: string; deger: string | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-notr-600">{etiket}</dt>
      {/*
        `null` icin "girilmemis" YAZILIYOR, tire degil: tek bir "—"
        okuyucuya "bos mu, yoksa gorunmuyor mu" sorusunu birakiyor.
      */}
      <dd className={deger === null ? "text-notr-600 italic" : "text-notr-900"}>
        {deger ?? "girilmemiş"}
      </dd>
    </div>
  );
}
